import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { api } from "@/api/client";
import { queryKeys } from "@/api/queryKeys";
import type { DataResponse, MaterialSummary } from "@/api/schemas";
import type { components } from "@/api/types.gen";
import { useUndoOptional } from "@/lib/undo/UndoContext";

type S = components["schemas"];
export type LaminovanieRequest = S["LaminovanieRequest"];
export type LaminovanieResponse = S["LaminovanieResponse"];

/** Lamináty v dekore dosky — prvé s jej štruktúrou (KOLO 234). */
export function useLaminatyKDoske(
  materialId: string | null | undefined,
): UseQueryResult<DataResponse<MaterialSummary[]>> {
  return useQuery({
    queryKey: ["cennik", "laminaty-k-doske", materialId ?? ""],
    queryFn: async () => {
      const { data } = await api.get<DataResponse<MaterialSummary[]>>(
        `/cennik/laminaty-k-doske/${materialId}`,
      );
      return data;
    },
    enabled: !!materialId,
  });
}

async function invaliduj(
  qc: ReturnType<typeof useQueryClient>,
  zakazkaId: string,
  variantaId: string,
): Promise<void> {
  await Promise.all([
    qc.invalidateQueries({ queryKey: queryKeys.zakazky.detail(zakazkaId) }),
    qc.invalidateQueries({ queryKey: queryKeys.zakazky.porez(variantaId) }),
  ]);
}

/**
 * Laminovať dosky (KOLO 234) — laminát na jednu alebo obe strany, krok
 * späť jedným Ctrl+Z (nové lamináty preč, nahradené späť).
 */
export function useLaminovat(
  zakazkaId: string,
  variantaId: string,
): UseMutationResult<DataResponse<LaminovanieResponse>, Error, LaminovanieRequest> {
  const qc = useQueryClient();
  const undo = useUndoOptional();
  return useMutation({
    mutationFn: async (payload: LaminovanieRequest) => {
      const { data } = await api.post<DataResponse<LaminovanieResponse>>(
        `/varianty/${variantaId}/laminovanie`,
        payload,
      );
      return data;
    },
    onSuccess: async (vysledok) => {
      const { vytvorene, zmazane } = vysledok.data;
      undo?.pridaj({
        popis: "laminovanie",
        vrat: async () => {
          await api.post(`/varianty/${variantaId}/laminovanie/vratit`, {
            vytvorene,
            zmazane,
          });
          await invaliduj(qc, zakazkaId, variantaId);
        },
      });
      await invaliduj(qc, zakazkaId, variantaId);
    },
  });
}
